import React, { useState, useEffect } from "react";
import { api } from "../api/axios";
import Select from "react-select";
import {
    Plus,
    X,
    Package,
    Image,
    CheckCircle2,
    DollarSign,
    CalendarDays,
    Loader2,
    Star,
    Film,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generatePlanCode } from "../utils/helper";

const emptyForm = {
    name: "",
    dailyPostLimit: "",
    dailyPhotoLimit: "",
    features: [],
    plans: [{ planCode: "", name: "", price: "", days: "" }],
    star: 0,
    videoEnabled: false,
    videoLimit: 0,
    videoLimitPeriod: "day",
    maxVideoDurationSeconds: 30,
    videoQuality: "low",
};

export default function PackageCreateForm({ editData, onDone }) {
    const isEdit = Boolean(editData?._id);
    const [formData, setFormData] = useState(emptyForm);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editData) {
            setFormData({
                ...emptyForm,
                ...editData,
                plans: editData.plans?.length ? editData.plans : emptyForm.plans,
                features: editData.features ?? [],
            });
        } else {
            setFormData(emptyForm);
        }
    }, [editData]);

    const featureOptions = [
        { value: "Priority Support", label: "Priority Support" },
        { value: "Verified Badge", label: "Verified Badge" },
        { value: "Ad-Free Experience", label: "Ad-Free Experience" },
        { value: "Unlimited Chats", label: "Unlimited Chats" },
        { value: "Advanced Analytics", label: "Advanced Analytics" },
    ];

    const handleFeatureChange = (selectedOptions) => {
        setFormData((prev) => ({
            ...prev,
            features: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePlanChange = (index, field, value) => {
        const updated = [...formData.plans];
        updated[index][field] = value;
        setFormData((prev) => ({ ...prev, plans: updated }));
    };

    const addPlan = () => {
        setFormData((prev) => ({
            ...prev,
            plans: [...prev.plans, { planCode: "", name: "", price: "", days: "" }],
        }));
    };

    const removePlan = (index) => {
        const updated = formData.plans.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, plans: updated }));
    };

    // ─────────────── Validation ───────────────
    const validateForm = () => {
        if (!formData.name.trim()) return toast.error("Please enter a package name.");
        if (formData.dailyPostLimit === "" || formData.dailyPostLimit < 0)
            return toast.error("Enter a valid daily post limit.");
        if (formData.dailyPhotoLimit === "" || formData.dailyPhotoLimit < 0)
            return toast.error("Enter a valid daily photo limit.");
        if (formData.star < 0 || formData.star > 5)
            return toast.error("Star rating must be between 0 and 5.");
        if (formData.plans.length === 0) return toast.error("Add at least one plan.");

        for (const plan of formData.plans) {
            if (!plan.name.trim()) return toast.error("Each plan must have a name.");
            if (plan.price === "" || plan.price < 0)
                return toast.error("Each plan must have a valid price.");
            if (plan.days === "" || plan.days <= 0)
                return toast.error("Each plan must have a valid duration.");
        }
        return true;
    };

    // ─────────────── Submit ───────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        try {
            const payload = {
                ...formData,
                videoLimit: Number(formData.videoLimit) || 0,
                maxVideoDurationSeconds: Number(formData.maxVideoDurationSeconds) || 0,
                plans: formData.plans.map((plan) => ({
                    ...plan,
                    planCode: plan.planCode || generatePlanCode(formData.name, plan.name, plan.price),
                })),
            };

            if (isEdit) {
                await api.put(`/packages/${editData._id}`, payload);
                toast.success("✅ Package updated!");
            } else {
                await api.post("/packages", payload);
                toast.success("✅ Package created successfully!");
                setFormData(emptyForm);
            }
            onDone?.();
        } catch (error) {
            console.error(error);
            toast.error(isEdit ? "❌ Error updating package." : "❌ Error creating package. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto bg-white shadow-md rounded-2xl p-6 mt-10 border border-gray-200">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar theme="colored" />
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 mb-6">
                <Package className="w-6 h-6 text-green-500" /> {isEdit ? "Edit Package" : "Create New Package"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Package Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Package Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-green-500 outline-none"
                        placeholder="e.g., Silver, Gold, Platinum"
                    />
                </div>

                {/* Star + Daily Limits */}
                <div className="grid grid-cols-3 gap-4">
                    {/* ⭐ Star Rating Input */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500" /> Star Rating (0–5)
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min={0}
                                max={5}
                                name="star"
                                value={formData.star}
                                onChange={handleChange}
                                className="w-20 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-yellow-400 outline-none"
                            />
                            <div className="flex gap-0.5">
                                {Array.from({ length: 5 }, (_, i) => (
                                    <Star
                                        key={i}
                                        className={`w-5 h-5 ${i < formData.star
                                            ? "text-yellow-400 fill-yellow-400"
                                            : "text-gray-300"
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Daily Post Limit */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-500" /> Daily Post Limit
                        </label>
                        <input
                            type="number"
                            min={0}
                            name="dailyPostLimit"
                            value={formData.dailyPostLimit}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-green-500 outline-none"
                        />
                    </div>

                    {/* Daily Photo Limit */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Image className="w-4 h-4 text-blue-500" /> Daily Photo Limit
                        </label>
                        <input
                            type="number"
                            min={0}
                            name="dailyPhotoLimit"
                            value={formData.dailyPhotoLimit}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                </div>

                {/* Features Select */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Features
                    </label>
                    <Select
                        isMulti
                        name="features"
                        options={featureOptions}
                        classNamePrefix="select"
                        value={featureOptions.filter((opt) =>
                            formData.features.includes(opt.value)
                        )}
                        onChange={handleFeatureChange}
                        placeholder="Select features..."
                        styles={{
                            control: (base) => ({
                                ...base,
                                backgroundColor: "white",
                                borderColor: "#d1d5db",
                                borderRadius: "0.5rem",
                                padding: "2px",
                            }),
                        }}
                    />
                </div>

                {/* Video Settings */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <label className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Film className="w-4 h-4 text-purple-600" /> Video Uploads
                    </label>
                    <div className="flex items-center gap-2 mb-3">
                        <input
                            type="checkbox"
                            id="videoEnabled"
                            checked={!!formData.videoEnabled}
                            onChange={(e) => setFormData(prev => ({ ...prev, videoEnabled: e.target.checked }))}
                        />
                        <label htmlFor="videoEnabled" className="text-sm text-gray-700">
                            Allow video uploads on this package
                        </label>
                    </div>

                    {formData.videoEnabled && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-600 block mb-1">Videos allowed</label>
                                <input
                                    type="number"
                                    min={0}
                                    name="videoLimit"
                                    value={formData.videoLimit}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 block mb-1">Reset period</label>
                                <select
                                    name="videoLimitPeriod"
                                    value={formData.videoLimitPeriod}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                >
                                    <option value="day">per day</option>
                                    <option value="week">per week</option>
                                    <option value="month">per month</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 block mb-1">Max video length (seconds)</label>
                                <input
                                    type="number"
                                    min={0}
                                    name="maxVideoDurationSeconds"
                                    value={formData.maxVideoDurationSeconds}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 block mb-1">Quality</label>
                                <select
                                    name="videoQuality"
                                    value={formData.videoQuality}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                >
                                    <option value="low">Low (compressed)</option>
                                    <option value="hd">HD</option>
                                    <option value="raw">Ultra HD / Raw</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Plans Section */}
                <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-yellow-500" /> Plans
                    </label>

                    <div className="space-y-4">
                        {formData.plans.map((plan, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-row gap-4 items-end"
                            >
                                {/* Plan Name */}
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Plan Name
                                    </label>
                                    <input
                                        type="text"
                                        value={plan.name}
                                        onChange={(e) =>
                                            handlePlanChange(index, "name", e.target.value)
                                        }
                                        placeholder="e.g., Monthly, Yearly"
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-green-400 outline-none"
                                    />
                                </div>

                                {/* Plan Price */}
                                <div className="w-24">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={plan.price}
                                        onChange={(e) =>
                                            handlePlanChange(index, "price", e.target.value)
                                        }
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-green-400 outline-none"
                                    />
                                </div>

                                {/* Plan Duration */}
                                <div className="w-36">
                                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <CalendarDays className="w-4 h-4 text-green-500" /> Days
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={plan.days}
                                        onChange={(e) =>
                                            handlePlanChange(index, "days", e.target.value)
                                        }
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-green-400 outline-none"
                                    />
                                </div>

                                {formData.plans.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removePlan(index)}
                                        className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Add Plan Button */}
                    <button
                        type="button"
                        onClick={addPlan}
                        className="mt-3 flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm"
                    >
                        <Plus className="w-4 h-4" /> Add Plan
                    </button>
                </div>

                {/* Submit */}
                <div className="pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex items-center justify-center gap-2 rounded-lg font-medium px-4 py-2 text-white ${loading
                            ? "bg-green-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                            }`}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" /> {isEdit ? "Saving..." : "Creating Package..."}
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="w-5 h-5" /> {isEdit ? "Save Changes" : "Save Package"}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
